"use client";

import React, { useCallback, useEffect, useRef, useState } from "react";
import { ChevronDown, ChevronRight, XCircle } from "lucide-react";



import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import Icons from "../../icons/icons";
import { cn } from "../../lib/utils";
import { DataModel } from "../../types/tree-view-types";
import { expandParentsOfSpan, getExceptionType } from "../../utils/tree-view-utils";
import NodeLabel from "./node-label";
import TracePaginationControls from "./trace-pagination-controls";


interface TreeViewProps {
  data: DataModel[];
  className?: string;
  onClick: (span: string) => void;
  setExceptionSearchTerm: React.Dispatch<React.SetStateAction<string>>;
  selectedSpanId: string;
  onTraceExpand?: (trace: any) => void;
  allTraces?: any[];
  allTracesForFactId?: any[];
  isGrouped?: boolean;
  loadingTraceId: string | null;
  setLoadingTraceId: React.Dispatch<React.SetStateAction<string | null>>;
  errorTraceId: string | null;
  errorTraceMessage: string | null;
  factId?: string | null;
  tokenCount?: string;
  tokenTooltip?: string;
  onWorkflowNameClick?: (workflowName: string) => void;
  // Server-paginated grouped facts: show the fact's traces a page at a time.
  enableTracePagination?: boolean;
  tracePageSize?: number;
  tracesTotalCount?: number | null;
  tracesNextPageToken?: string | null;
  tracesLoadingMore?: boolean;
  onLoadMoreTraces?: () => void;
}

// Pagination state handed to the fact node so it can render page controls
// around its (already sliced) trace children.
interface FactPagination {
  page: number;
  pageSize: number;
  // Real total (fact_count); null when the backend only sends a cursor.
  total: number | null;
  // How many traces are currently loaded client-side.
  loadedCount: number;
  hasPrev: boolean;
  hasNext: boolean;
  loadingMore: boolean;
  onPrev: () => void;
  onNext: () => void;
}

interface NodeProps {
  node: DataModel;
  level: number;
  isFirst: boolean;
  isLast: boolean;
  onToggle: (nodeId: string) => void;
  expandedNodes: Set<string>;
  getNodeHeight: (nodeId: string) => number;
  onClick: (span: string) => void;
  hasError: boolean;
  handleToggleAll: () => void;
  currentToggleState: "expand" | "collapse";
  setExceptionSearchTerm: React.Dispatch<React.SetStateAction<string>>;
  selectedId: string | null;
  nodeHeights: Map<string, number>;
  setNodeHeight: (nodeId: string, height: number) => void;
  hasParent?: boolean;
  loadingTraceId: string | null;
  tokenCount?: string;
  tokenTooltip?: string;
  errorTraceId: string | null;
  errorTraceMessage: string | null;
  onWorkflowNameClick?: (workflowName: string) => void;
  factPagination?: FactPagination;
}

const DEFAULT_NODE_HEIGHT = 54; // Updated default height for multi-line
const MULTILINE_FIRST_ROW_CENTER = 20; // Center of the first line for multi-line nodes
// Fallback traces-per-page when the host app doesn't pass `tracePageSize`
// (e.g. the VS Code webview). Portal-ux overrides this via the prop.
const DEFAULT_TRACE_PAGE_SIZE = 10;
const CURVE_Y_OFFSET = 8; //height taken by curve in the line on y-axis
const INDENT_WIDTH = 24;
const CHEVRON_OFFSET = 16;
const LEFT_PADDING_OFFSET = 24;
// Fixed height of a pagination control row, so its connector curve can be
// centered deterministically (it draws the same tree line as a child node).
const TRACE_PAGINATION_ROW_HEIGHT = 40;
const multiLineSpanTypes = [
  "agentic.tool.invocation",
  "agentic.mcp.invocation",
  "agentic.delegation",
  "inference.framework",
  "inference",
  "retrieval",
  "agentic.turn",
];
const nonExactMatchColorFromGanttChart = "rgba(50, 188, 175,0.3)"; // used in gantt chart for non-exact matches, helps in deciding the opacity in tree view

// Helper function to calculate height up to the last direct child
const calculateSubtreeHeight = (
  node: DataModel,
  expandedNodes: Set<string>,
  nodeHeights: Map<string, number>
): number => {
  const nodeHeight = nodeHeights.get(node.id) || DEFAULT_NODE_HEIGHT;
  let height = nodeHeight;

  if (node.children && expandedNodes.has(node.id)) {
    for (let i = 0; i < node.children.length; i++) {
      const child = node.children[i];
      const isLastChild = i === node.children.length - 1;
      const childHeight = nodeHeights.get(child.id) || DEFAULT_NODE_HEIGHT;
      height += childHeight; //take full height of child initially
      if (isLastChild) {
        height -= childHeight; //remove full height
        // Add partial height based on whether it's multi-line or single-line
        const isMultiLineLabel = multiLineSpanTypes.includes(
          child.spanType || ""
        );
        if (isMultiLineLabel) {
          height += childHeight / 3 - CURVE_Y_OFFSET;
        } else {
          height += childHeight / 2 - CURVE_Y_OFFSET;
        }
      } else if (child.children && expandedNodes.has(child.id)) {
        // Add height of all visible descendants
        height += calculateVisibleDescendantsHeight(
          child,
          expandedNodes,
          nodeHeights
        );
      }
    }
  }

  return height;
};

// Helper function to calculate height of all visible descendants
const calculateVisibleDescendantsHeight = (
  node: DataModel,
  expandedNodes: Set<string>,
  nodeHeights: Map<string, number>
): number => {
  let height = 0;

  if (node.children && expandedNodes.has(node.id)) {
    for (const child of node.children) {
      const childHeight = nodeHeights.get(child.id) || DEFAULT_NODE_HEIGHT;
      height += childHeight;

      if (child.children && expandedNodes.has(child.id)) {
        height += calculateVisibleDescendantsHeight(
          child,
          expandedNodes,
          nodeHeights
        );
      }
    }
  }

  return height;
};

// A pagination control rendered as a tree row: fixed height, indented like a
// child node, with the same connector line (vertical spine segment + horizontal
// + rounded curve) so it hangs off the spine exactly like a trace row.
const FactPaginationRow: React.FC<{
  position: "top" | "bottom";
  level: number;
  pagination: FactPagination;
}> = ({ position, level, pagination }) => {
  const childLevel = level + 1;
  const vx = childLevel * INDENT_WIDTH - 16; // spine x (matches child nodes)
  const center = TRACE_PAGINATION_ROW_HEIGHT / 2; // curve / horizontal y
  // The bottom control is the visual last row, so its spine segment stops at the
  // curve; the top control is mid-spine, so it runs the full row height.
  const isLastRow = position === "bottom";

  return (
    <div
      className="relative"
      style={{ height: `${TRACE_PAGINATION_ROW_HEIGHT}px` }}
    >
      <svg
        className="pointer-events-none absolute left-0 top-0 overflow-visible"
        style={{
          width: "100%",
          height: `${TRACE_PAGINATION_ROW_HEIGHT}px`,
          paddingLeft: `${LEFT_PADDING_OFFSET}px`,
        }}
        shapeRendering="crispEdges"
      >
        {/* Vertical spine segment */}
        <line
          x1={vx}
          y1={0}
          x2={vx}
          y2={isLastRow ? CURVE_Y_OFFSET : TRACE_PAGINATION_ROW_HEIGHT}
          stroke="#4B5563"
          strokeWidth={1}
        />
        {/* Horizontal line to the control */}
        <line
          x1={childLevel * INDENT_WIDTH - 8}
          y1={center}
          x2={childLevel * INDENT_WIDTH + 8}
          y2={center}
          stroke="#4B5563"
          strokeWidth={1}
        />
        {/* Rounded corner */}
        <path
          d={`M ${vx} ${CURVE_Y_OFFSET} Q ${childLevel * INDENT_WIDTH - 18} ${center} ${childLevel * INDENT_WIDTH - 8
            } ${center}`}
          stroke="#4B5563"
          strokeWidth={1}
          fill="none"
        />
      </svg>
      <div
        className="flex h-full items-center"
        style={{
          paddingLeft: `${childLevel * INDENT_WIDTH + LEFT_PADDING_OFFSET + 8}px`,
        }}
      >
        <TracePaginationControls
          position={position}
          page={pagination.page}
          pageSize={pagination.pageSize}
          total={pagination.total}
          loadedCount={pagination.loadedCount}
          hasPrev={pagination.hasPrev}
          hasNext={pagination.hasNext}
          loadingMore={pagination.loadingMore}
          onPrev={pagination.onPrev}
          onNext={pagination.onNext}
        />
      </div>
    </div>
  );
};

// Individual tree node component
const TreeNodeComponent: React.FC<NodeProps> = ({
  node,
  level,
  isFirst,
  isLast,
  onToggle,
  expandedNodes,
  getNodeHeight,
  onClick,
  hasError,
  handleToggleAll,
  currentToggleState,
  selectedId,
  setExceptionSearchTerm,
  nodeHeights,
  setNodeHeight,
  hasParent = false,
  loadingTraceId,
  tokenCount,
  tokenTooltip,
  errorTraceId,
  errorTraceMessage,
  onWorkflowNameClick,
  factPagination,
}) => {
  const nodeRef = useRef<HTMLDivElement>(null);
  const hasChildren = node.children && node.children.length > 0;
  const isExpanded = expandedNodes.has(node.id);
  const isCollapsedTrace = node.spanType === 'workflow' && node.children?.length === 0 && node.entityCount === undefined; // Placeholder node
  const showChevron = isCollapsedTrace || hasChildren;
  const showChildren = hasChildren && isExpanded;

  const isMultiLine = multiLineSpanTypes.includes(node.spanType || "");

  // Measure node height after render
  const previousHeight = useRef<number>(0);

  useEffect(() => {
    if (!nodeRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.target === nodeRef.current) {
          const height = (entry.target as HTMLElement).offsetHeight;
          if (Math.abs(height - previousHeight.current) > 1) {
            previousHeight.current = height;
            setNodeHeight(node.id, height);
          }
        }
      }
    });

    resizeObserver.observe(nodeRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [node.id, setNodeHeight]);

  useEffect(() => {
    // Set data.input on the first load for the root node.
    if (level === 0 && isFirst) {
      setExceptionSearchTerm("data.input");
    }
  }, []);

  const handleToggle = (e: React.MouseEvent<HTMLDivElement>) => {
    if (hasChildren || isCollapsedTrace) {
      e.stopPropagation();  //to not trigger onClick of the node
      onToggle(node.id);
    }
  };

  const currentNodeHeight = nodeHeights.get(node.id) || DEFAULT_NODE_HEIGHT;

  // Calculate total height of this node's subtree when expanded
  const subtreeHeight = showChildren
    ? calculateSubtreeHeight(node, expandedNodes, nodeHeights) -
    currentNodeHeight
    : 0;

  const handleSelect = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (node.spanType === 'fact') {
      return;
    }

    setExceptionSearchTerm("data.input");
    onClick(id);
  };

  const errorClickHandler = (node: DataModel, e: React.MouseEvent) => {
    e.stopPropagation();
    handleSelect(node.id, e);
    // When the badge fell back to the span's status (no exception event),
    // highlight the matching status field instead of "exception.message".
    const errorKind = getExceptionType(node)?.kind;
    setExceptionSearchTerm(
      errorKind === "status"
        ? "message"
        : errorKind === "statusDescription"
          ? "description"
          : "exception.message"
    );
  };

  const mcpClickHandler = (node: DataModel, e: React.MouseEvent) => {
    e.stopPropagation();
    handleSelect(node.id, e);
    setExceptionSearchTerm("tool.mcp");
  };

  const metadataClickHandler = (node: DataModel, property: string, e: React.MouseEvent) => {
    e.stopPropagation();
    handleSelect(node.id, e);
    setExceptionSearchTerm(property);
  };

  // Calculate vertical center of the node for line connections
  const nodeCenter = currentNodeHeight / 2;
  const firstRowCenter = MULTILINE_FIRST_ROW_CENTER;

  return (
    <div className="relative min-w-full">
      {/* Node content */}
      <div
        ref={nodeRef}
        className={cn(
          "relative z-10 w-full flex items-center py-2 text-body text-gray-300 transition-colors hover:text-gray-100 cursor-pointer",
          {
            "bg-[#FFFFFF14] text-[#C1C9CF]": selectedId === node.id,
            "bg-[#34BCAF29]": selectedId === node.id && isMultiLine,
          }
        )}
        style={{
          paddingLeft: `${level * INDENT_WIDTH +
            (isFirst ? 24 : CHEVRON_OFFSET + 8) +
            LEFT_PADDING_OFFSET
            }px`,
        }}
        onClick={(e) => handleSelect(node.id, e)}
      >
        {/* Chevron icon */}
        {!isFirst && showChevron && (
          <div
            className="absolute mr-2 flex h-4 w-4 items-center justify-center"
            style={{
              left: `${level * INDENT_WIDTH + LEFT_PADDING_OFFSET}px`,
              top: `${isMultiLine ? firstRowCenter - 8 : nodeCenter - 8}px`, // place at 1/3rd height adjusting its height
            }}
            onClick={handleToggle}
          >
            {isExpanded ? (
              <ChevronDown size={12} className="text-[#C1C9CF]" />
            ) : (
              <ChevronRight size={12} className="text-[#8C9EA1]" />
            )}
          </div>
        )}

        {/* Node label */}
        <div
          className={cn("flex-1 text-nowrap", {
            "opacity-40":
              node.isFiltered ||
              node.color === nonExactMatchColorFromGanttChart,
          })}
        >
          <div className="flex items-center gap-2">
            <NodeLabel
              node={node}
              isFirst={isFirst}
              errorClickHandler={errorClickHandler}
              mcpClickHandler={mcpClickHandler}
              metadataClickHandler={metadataClickHandler}
              hasError={hasError}
              selectedNodeId={selectedId}
              tokenCount={tokenCount}
              tokenTooltip={tokenTooltip}
              onWorkflowNameClick={onWorkflowNameClick}
            />
            {/* show loader when fetching spans*/}
            {loadingTraceId === node.id && (
              <span className="text-xs text-gray-400">Loading...</span>
            )}
            {/* show error icon when span fetch failed */}
            {errorTraceId === node.id && loadingTraceId !== node.id && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <XCircle size={14} className="text-red-500" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{errorTraceMessage || 'Error loading traces'}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </div>
      </div>

      {/* Toggle all button for first node */}
      {isFirst && (
        <span
          className="absolute left-0 top-2 z-10 cursor-pointer"
          onClick={(event) => {
            event.stopPropagation();
            handleToggleAll();
          }}
          style={{
            marginLeft: `${LEFT_PADDING_OFFSET}px`,
            top: `${nodeCenter - 8}px`, // Center vertically, subtract half icon height.
          }}
        >
          {currentToggleState === "collapse"
            ? Icons.ExpandAllIcon("w-4 h-4")
            : Icons.CollapseAllIcon("w-4 h-4")}
        </span>
      )}

      {/* SVG Container for all lines */}
      <svg
        className="pointer-events-none absolute left-0 top-0 overflow-visible"
        style={{
          width: "100%",
          height: `${currentNodeHeight + subtreeHeight}px`,
          paddingLeft: `${LEFT_PADDING_OFFSET}px`,
        }}
        shapeRendering="crispEdges"
      >
        {/* First node: vertical line down */}
        {isFirst && showChildren && (
          <line
            x1={8}
            y1={nodeCenter + 16} // start with 16px offset to keep below chevron
            x2={8}
            y2={nodeCenter + 16 + subtreeHeight} //take full subtree height from starting point
            stroke="#4B5563"
            strokeWidth={1}
          />
        )}

        {/* Non-first nodes: connecting lines */}
        {!isFirst && (
          <>
            {/* Vertical line from parent node to current node.*/}
            {hasParent && (
              <line
                x1={level * INDENT_WIDTH - 16}
                y1={0} //always start from top of current node.
                x2={level * INDENT_WIDTH - 16}
                y2={isLast ? CURVE_Y_OFFSET : currentNodeHeight} //if last, stop at curve offset, else go full height
                stroke="#4B5563"
                strokeWidth={1}
              />
            )}
            {/* Horizontal line to node */}
            {hasParent && (
              <line
                x1={level * INDENT_WIDTH - 8}
                y1={isMultiLine ? firstRowCenter : nodeCenter} //put on 1/3 for multi-line nodes
                x2={level * INDENT_WIDTH + (hasChildren ? 0 : 8)}
                y2={isMultiLine ? firstRowCenter : nodeCenter}
                stroke="#4B5563"
                strokeWidth={1}
              />
            )}
            {/* Rounded corner */}
            {/*M is move to x1,y1 //Q is quadratic Bezier curve to x2,y2 with
            control point cx,cy*/}
            {hasParent && (
              <path
                d={`M ${level * INDENT_WIDTH - 16} ${CURVE_Y_OFFSET} Q ${level * INDENT_WIDTH - 18
                  } ${isMultiLine ? firstRowCenter : nodeCenter} ${level * INDENT_WIDTH - 8
                  } ${isMultiLine ? firstRowCenter : nodeCenter}`}
                stroke="#4B5563"
                strokeWidth={1}
                fill="none"
              />
            )}
            {/* Vertical line down to children of current node */}
            {showChildren && (
              <line
                x1={level * INDENT_WIDTH + 8}
                y1={isMultiLine ? firstRowCenter + 16 : nodeCenter + 16} // start with 16px offset to avoid chevron in current node.
                x2={level * INDENT_WIDTH + 8}
                y2={subtreeHeight + currentNodeHeight}
                stroke="#4B5563"
                strokeWidth={1}
              />
            )}
          </>
        )}
      </svg>

      {/* Children */}
      {showChildren && node.children && (
        <div className="relative">
          {factPagination?.hasPrev && (
            <FactPaginationRow
              position="top"
              level={level}
              pagination={factPagination}
            />
          )}
          {node.children.map((child, index) => (
            <TreeNodeComponent
              key={child.id}
              node={child}
              level={level + 1}
              isFirst={false}
              // When a bottom pagination control follows, the last child is no
              // longer the visual last row — keep its spine running to the control.
              isLast={
                index === node.children!.length - 1 && !factPagination?.hasNext
              }
              onToggle={onToggle}
              expandedNodes={expandedNodes}
              getNodeHeight={getNodeHeight}
              onClick={onClick}
              hasError={child.statusCode !== "ok"}
              handleToggleAll={handleToggleAll}
              currentToggleState={currentToggleState}
              selectedId={selectedId}
              setExceptionSearchTerm={setExceptionSearchTerm}
              nodeHeights={nodeHeights}
              setNodeHeight={setNodeHeight}
              hasParent={true}
              loadingTraceId={loadingTraceId}
              errorTraceId={errorTraceId}
              errorTraceMessage={errorTraceMessage}
              onWorkflowNameClick={onWorkflowNameClick}
            />
          ))}
          {factPagination?.hasNext && (
            <FactPaginationRow
              position="bottom"
              level={level}
              pagination={factPagination}
            />
          )}
        </div>
      )}
    </div>
  );
};

// Main TreeView component
const TreeView: React.FC<TreeViewProps> = ({
  data,
  className = "",
  onClick,
  setExceptionSearchTerm,
  selectedSpanId,
  onTraceExpand,
  allTracesForFactId,
  loadingTraceId,
  setLoadingTraceId,
  tokenCount,
  tokenTooltip,
  errorTraceId,
  errorTraceMessage,
  onWorkflowNameClick,
  factId,
  enableTracePagination,
  tracePageSize,
  tracesTotalCount,
  tracesNextPageToken,
  tracesLoadingMore,
  onLoadMoreTraces,
}) => {
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set([]));
  const [currentToggleState, setCurrentToggleState] = useState<
    "expand" | "collapse"
  >("collapse");
  const [nodeHeights, setNodeHeights] = useState<Map<string, number>>(
    new Map()
  );
  // Current page of the fact node's trace children (0-based).
  const [tracePage, setTracePage] = useState(0);
  // When advancing past loaded data we fetch more, then advance once it arrives.
  // Holds the loaded-trace count captured at fetch time (null = not awaiting a
  // fetch). We advance when loadedCount grows past it — keyed on the data itself
  // rather than the loading flag, which flips before the appended data lands.
  const advanceAfterLoadRef = useRef<number | null>(null);

  // Reset pagination whenever the displayed fact changes.
  useEffect(() => {
    setTracePage(0);
    advanceAfterLoadRef.current = null;
  }, [factId]);

  const setNodeHeight = useCallback((nodeId: string, height: number) => {
    setNodeHeights((prev) => {
      const currentHeight = prev.get(nodeId);
      if (currentHeight === height) return prev;
      const newMap = new Map(prev);
      newMap.set(nodeId, height);
      return newMap;
    });
  }, []);

  // Track previous tree structure
  const previousTraceIdsRef = useRef<string>('');

  useEffect(() => {
    if (data.length === 0) return;

    const getStructureId = (nodes: DataModel[]): string => {
      return nodes.map(node => {
        if (node.spanType === 'fact' && node.children) {
          const traceIds = node.children.map(c => c.id).sort().join(',');
          return `fact:${node.id}:[${traceIds}]`;
        }
        return node.id;
      }).join('|');
    };

    const currentStructure = getStructureId(data);

    // Only reset expansion state when structure actually changes
    if (previousTraceIdsRef.current !== currentStructure) {
      previousTraceIdsRef.current = currentStructure;
      setExpansionStateToInitial();
    }
  }, [data]);

  useEffect(() => {
    if (!selectedSpanId) return;
    if (expandedNodes.has(selectedSpanId)) return;

    const parentsToExpand = expandParentsOfSpan(selectedSpanId, data);
    setExpandedNodes(prev => {
      const newSet = new Set(prev);
      parentsToExpand.forEach(id => newSet.add(id));
      return newSet;
    });
  }, [selectedSpanId]);

  // Auto-expand loaded trace after spans are fetched
  useEffect(() => {
    if (loadingTraceId && data.length > 0) {
      const findNode = (nodes: DataModel[]): DataModel | null => {
        for (const node of nodes) {
          if (node.id === loadingTraceId) return node;
          if (node.children) {
            const found = findNode(node.children);
            if (found) return found;
          }
        }
        return null;
      };

      const loadedNode = findNode(data);

      if (loadedNode && loadedNode.entityCount !== undefined) { // check if node data has been fetched
        if (loadedNode.children && loadedNode.children.length > 0) { // check if node has children before triggering expansion
          setExpandedNodes(prev => {
            const newSet = new Set(prev);
            newSet.add(loadingTraceId);

            // Expand 3 levels
            const expandLevels = (n: DataModel, depth: number, currentDepth: number = 0) => {
              if (depth !== -1 && currentDepth >= depth) return;
              if (n.children) {
                n.children.forEach(child => {
                  newSet.add(child.id);
                  expandLevels(child, depth, currentDepth + 1);
                });
              }
            };
            expandLevels(loadedNode, 3, 0);

            return newSet;
          });
          onClick(loadingTraceId);
        }
        setLoadingTraceId(null);
      }
    }
  }, [data, loadingTraceId]);

  // Collapse the errored node to reset chevron
  useEffect(() => {
    if (errorTraceId) {
      setExpandedNodes(prev => {
        const newSet = new Set(prev);
        newSet.delete(errorTraceId);
        return newSet;
      });
    }
  }, [errorTraceId]);

  // Auto-load first trace on mount for grouped mode
  useEffect(() => {
    if (data.length === 0) return;

    const factNode = data.find(node => node.spanType === 'fact');
    if (factNode && factNode.children && factNode.children.length > 0) {
      const firstTrace = factNode.children[0];

      if ((!firstTrace.children || firstTrace.children.length === 0) && firstTrace.spanType === 'workflow') {
        if (onTraceExpand && allTracesForFactId && !loadingTraceId) {
          const trace = allTracesForFactId.find(t => t.trace_id === firstTrace.id);
          if (trace) {
            setLoadingTraceId(firstTrace.id);
            onTraceExpand(trace);
          }
        }
      }
    }
  }, [data]);

  const setExpansionStateToInitial = () => {
    const getAllIds = (nodes: DataModel[], depth: number, currentDepth = 0): string[] => {
      if (depth !== -1 && currentDepth >= depth) return [];
      const ids: string[] = [];
      for (const node of nodes) {
        if (node.spanType === 'fact') {
          ids.push(node.id);

          if (node.children && node.children.length > 0) {
            const firstChild = node.children[0];
            ids.push(firstChild.id); // first trace node under fact

            if (firstChild.children && firstChild.children.length > 0) {
              ids.push(...getAllIds(firstChild.children, 3, 0));
            }
          }
        } else {
          if (node.children && node.children.length > 0) {
            ids.push(node.id);
            ids.push(...getAllIds(node.children, depth, currentDepth + 1));
          }
        }
      }
      return ids;
    };

    setExpandedNodes(new Set<string>(getAllIds(data, 3)));
  };

  const handleToggle = (nodeId: string) => {
    setExpandedNodes((prev) => {
      const newSet = new Set(prev);
      const wasExpanded = prev.has(nodeId);

      if (wasExpanded) {
        newSet.delete(nodeId);
      } else {
        newSet.add(nodeId);

        const findNode = (nodes: DataModel[]): DataModel | null => {
          for (const node of nodes) {
            if (node.id === nodeId) {
              return node;
            }
            if (node.children) {
              const found = findNode(node.children);
              if (found) return found;
            }
          }
          return null;
        };

        const nodeToExpand = findNode(data);

        if (
          nodeToExpand &&
          (!nodeToExpand.children || nodeToExpand.children.length === 0) &&
          nodeToExpand.spanType === 'workflow'
        ) {
          if (onTraceExpand && allTracesForFactId) {
            const trace = allTracesForFactId.find((t) => t.trace_id === nodeId);
            if (trace) {
              setLoadingTraceId(nodeId);
              onTraceExpand(trace);
            }
          }
        }
      }
      return newSet;
    });
  };
  const handleToggleAll = () => {
    if (currentToggleState === 'collapse') {
      // Recursively collect ALL IDs (no depth limit)
      const getAllIds = (nodes: DataModel[]): string[] => {
        const ids: string[] = [];
        for (const node of nodes) {
          ids.push(node.id);
          if (node.children && node.children.length > 0) {
            ids.push(...getAllIds(node.children));
          }
        }
        return ids;
      };

      const factNode = data.find((node) => node.spanType === 'fact');

      if (factNode && factNode.children && factNode.children.length > 0) {
        const firstTraceNode = factNode.children[0];
        const idsToExpand = [
          factNode.id,
          firstTraceNode.id,
          ...getAllIds(firstTraceNode.children || []),
        ];
        setExpandedNodes(new Set(idsToExpand));
      } else {
        setExpandedNodes(new Set(getAllIds(data)));
      }
      setCurrentToggleState("expand");
    } else {
      setExpandedNodes(new Set([]));
      setCurrentToggleState("collapse");
    }
  };

  const getNodeHeight = (nodeId: string) => {
    return nodeHeights.get(nodeId) || DEFAULT_NODE_HEIGHT;
  };

  // ---- Trace pagination (fact node's trace children) -----------------------
  const factNode = enableTracePagination
    ? data.find((node) => node.spanType === "fact")
    : undefined;
  const factChildren = factNode?.children ?? [];
  const loadedCount = factChildren.length;
  // Real total is only known when the backend sends fact_count; with pure
  // cursor pagination (next_page_token only) it stays null and the controls
  // omit the "of Z" suffix.
  const knownTotal = tracesTotalCount ?? null;
  const hasMoreOnServer = !!tracesNextPageToken;
  // One knob: page size == the backend fetch batch size (falls back for hosts
  // that don't pass it, e.g. the VS Code webview).
  const effectivePageSize = tracePageSize ?? DEFAULT_TRACE_PAGE_SIZE;
  // Paginate when there's more than one page worth — whether that's already
  // loaded, known via fact_count, or signalled by a next_page_token cursor.
  const isTracePaginated =
    !!factNode &&
    (loadedCount > effectivePageSize ||
      hasMoreOnServer ||
      (knownTotal != null && knownTotal > effectivePageSize));

  const pageStart = tracePage * effectivePageSize;
  const pageEnd = pageStart + effectivePageSize;
  const hasPrev = tracePage > 0;
  const hasNext = pageEnd < loadedCount || hasMoreOnServer;

  const handleTracePrev = () => {
    if (hasPrev) setTracePage((p) => p - 1);
  };

  const handleTraceNext = () => {
    if (pageEnd < loadedCount) {
      // Next page is already loaded — just move forward.
      setTracePage((p) => p + 1);
    } else if (tracesNextPageToken && onLoadMoreTraces) {
      // Fetch the next chunk; advance once the new traces land (see effect below).
      advanceAfterLoadRef.current = loadedCount;
      onLoadMoreTraces();
    }
  };

  // Advance to the freshly loaded page once the appended traces arrive. Keyed on
  // loadedCount (not tracesLoadingMore) because the flag flips before the new
  // traces propagate into the tree data, which would otherwise drop the advance.
  useEffect(() => {
    if (
      advanceAfterLoadRef.current != null &&
      loadedCount > advanceAfterLoadRef.current
    ) {
      setTracePage((p) => p + 1);
      advanceAfterLoadRef.current = null;
    }
  }, [loadedCount]);

  // Render with the fact node's children sliced to the current page so the
  // subtree height/line maths stay correct for what's actually shown.
  const displayData = isTracePaginated
    ? data.map((node) =>
      node.id === factNode!.id
        ? { ...node, children: factChildren.slice(pageStart, pageEnd) }
        : node
    )
    : data;

  const factPagination: FactPagination | undefined = isTracePaginated
    ? {
      page: tracePage,
      pageSize: effectivePageSize,
      total: knownTotal,
      loadedCount,
      hasPrev,
      hasNext,
      loadingMore: !!tracesLoadingMore,
      onPrev: handleTracePrev,
      onNext: handleTraceNext,
    }
    : undefined;

  return (
    <div
      className={`h-full relative bg-inherit pb-2 font-worksans text-sm text-gray-300 ${className}`}
    >
      <div className="h-full overflow-auto min-w-full">
        {displayData.map((node, index) => (
          <TreeNodeComponent
            key={node.id}
            node={node}
            level={0}
            isFirst={index === 0}
            isLast={index === displayData.length - 1}
            onToggle={handleToggle}
            expandedNodes={expandedNodes}
            getNodeHeight={getNodeHeight}
            onClick={onClick}
            hasError={node.statusCode !== "ok"}
            handleToggleAll={handleToggleAll}
            currentToggleState={currentToggleState}
            selectedId={selectedSpanId}
            setExceptionSearchTerm={setExceptionSearchTerm}
            nodeHeights={nodeHeights}
            setNodeHeight={setNodeHeight}
            hasParent={false} // Top-level nodes have no parent, we use this prop to skip drawing parent lines
            loadingTraceId={loadingTraceId}
            tokenCount={index === 0 ? tokenCount : undefined}
            tokenTooltip={index === 0 ? tokenTooltip : undefined}
            errorTraceId={errorTraceId}
            errorTraceMessage={errorTraceMessage}
            onWorkflowNameClick={onWorkflowNameClick}
            factPagination={
              node.spanType === "fact" ? factPagination : undefined
            }
          />
        ))}
      </div>
    </div>
  );
};

export default TreeView;
